import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6e2c-1 — SPECIAL OPTIONS selector data layer (§33.10).
 *
 * Two GETs (read-only, fire on dialog mount) + three mutations (the Select
 * CRUD diff — fired ONLY on the user's explicit Select click, never on
 * mount/open/browse/toggle; §4 read-only data-safety + the userArmed gate).
 *
 * Legacy collections this replaces:
 *  - catalog:  ahu/mvc/collection/special.options.js → GET
 *    cms/1/v1.0.0/ahu/special_options/settings_ordered/ (`SpecialOptionsCollection`
 *    in the selector; stateStorage `ahuSpecialOptions` cache → RTKQ cache +
 *    a session-long keepUnusedDataFor here).
 *  - AHU rows: component_selector/.../collection/special_options.js (the
 *    `getOrFetch(ahu)` collection) → GET ahu/special_options/?ahu_unit_id=<id>
 *    (per-ahu cache → RTKQ tag).
 *
 * Select() (special_options.selector.js:172-204) does, per `changes{}` row:
 *  - existing + checked  → PATCH ahu/special_options/<rowId>/ {ahu_unit_id, qty}
 *  - existing + unchecked→ DELETE ahu/special_options/<rowId>/
 *  - new + checked       → POST  ahu/special_options/    {ahu_unit_id, special_option_id, qty}
 * One HTTP call per changed row, exactly like the Backbone destroy/save/create.
 */

/** settings_ordered catalog row (CMSAHUSpecialOptionSettingsSerializer). */
export interface ISpecialOptionCatalogRow {
  /** cms_s_o_s row id — the legacy build-match key (`opt.id`). */
  id: number;
  /** master_s_o id (special_option_name.id) — the checkbox/qty data-id and the
   * create payload `special_option_id`. */
  special_option_id: number;
  special_option_name?: string | null;
  special_option_label?: string | null;
  special_option_description?: string | null;
  special_option_category_id?: number | null;
  special_option_category_name?: string | null;
  mandatory?: boolean;
  checkbox?: boolean;
}

/** ahu/special_options row (a saved selection for this AHU). */
export interface IAhuSpecialOptionRow {
  /** ahu_special_options row id (the PATCH/DELETE target). */
  id: number;
  ahu_unit_id: number;
  special_option_id: number;
  qty: number | null;
}

const toCatalogRows = (res: unknown): ISpecialOptionCatalogRow[] =>
  Array.isArray(res) ? (res as ISpecialOptionCatalogRow[]) : [];

const toAhuRows = (res: unknown): IAhuSpecialOptionRow[] =>
  Array.isArray(res) ? (res as IAhuSpecialOptionRow[]) : [];

export const specialOptionsApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Catalog (settings_ordered) — global, session-long cache ----
    getSpecialOptionsCatalog: builder.query<ISpecialOptionCatalogRow[], void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/special_options/settings_ordered/',
      transformResponse: toCatalogRows,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // ---- AHU rows — per-ahu cache (getOrFetch equivalent) ----
    getAhuSpecialOptions: builder.query<IAhuSpecialOptionRow[], number>({
      query: (ahuId) => `/api/v1.0/ahu/special_options/?ahu_unit_id=${ahuId}`,
      transformResponse: toAhuRows,
      providesTags: (_r, _e, ahuId) => [{ type: 'AhuSpecialOptions', id: ahuId }],
    }),

    // ---- Select CRUD (explicit user click ONLY) ----
    createAhuSpecialOption: builder.mutation<
      IAhuSpecialOptionRow,
      { ahuUnitId: number; specialOptionId: number; qty: number }
    >({
      query: ({ ahuUnitId, specialOptionId, qty }) => ({
        url: '/api/v1.0/ahu/special_options/',
        method: 'POST',
        body: { ahu_unit_id: ahuUnitId, special_option_id: specialOptionId, qty },
      }),
      invalidatesTags: (_r, _e, { ahuUnitId }) => [
        { type: 'AhuSpecialOptions', id: ahuUnitId },
      ],
    }),
    patchAhuSpecialOption: builder.mutation<
      IAhuSpecialOptionRow,
      { rowId: number; ahuUnitId: number; qty: number }
    >({
      query: ({ rowId, ahuUnitId, qty }) => ({
        url: `/api/v1.0/ahu/special_options/${rowId}/`,
        method: 'PATCH',
        body: { ahu_unit_id: ahuUnitId, qty },
      }),
      invalidatesTags: (_r, _e, { ahuUnitId }) => [
        { type: 'AhuSpecialOptions', id: ahuUnitId },
      ],
    }),
    deleteAhuSpecialOption: builder.mutation<
      unknown,
      { rowId: number; ahuUnitId: number }
    >({
      query: ({ rowId }) => ({
        url: `/api/v1.0/ahu/special_options/${rowId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { ahuUnitId }) => [
        { type: 'AhuSpecialOptions', id: ahuUnitId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSpecialOptionsCatalogQuery,
  useGetAhuSpecialOptionsQuery,
  useCreateAhuSpecialOptionMutation,
  usePatchAhuSpecialOptionMutation,
  useDeleteAhuSpecialOptionMutation,
} = specialOptionsApi;
